import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { DroitsFins, RoleGalerie } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Équipe — `GET /v1/g/:slug/equipe` (`docs/dev/api.md` § 3, S2-k), l'envers de
 * `/auth/me` : qui travaille dans cette galerie. Sert ici à nommer les
 * comptes du journal du coffre (3.5) — le journal ne porte qu'un
 * `compteEmail` résolu à l'export ; l'équipe donne le nom affiché.
 */
export interface MembreEquipe {
  readonly compteId: string;
  readonly nom: string;
  readonly email: string;
  readonly role: RoleGalerie;
  readonly droits: DroitsFins;
  readonly support: boolean;
  readonly expireLe: string | null;
  readonly derniereConnexion: string | null;
}

export interface ReponseEquipe {
  readonly galerie: string;
  readonly membres: readonly MembreEquipe[];
  /** Révision d'équipe, renvoyée à chaque modification (409 si périmée). */
  readonly revision: number;
  /** `false` : module `roles-fins` coupé — droits conservés, non appliqués. */
  readonly droitsAppliques: boolean;
}

export interface ReponseModificationMembre {
  readonly membre: MembreEquipe | null;
  readonly revision: number;
}

const base = (galerie: string) => `/g/${encodeURIComponent(galerie)}/equipe`;

export const equipeApi = {
  lister: (galerie: string, appel?: AppelApi) =>
    api.get<ReponseEquipe>(base(galerie), { galerie, ...appel }),
  modifierRole: (
    galerie: string,
    compteId: string,
    corps: { role: RoleGalerie; revision: number },
    appel?: AppelApi,
  ) =>
    api.put<ReponseModificationMembre>(
      `${base(galerie)}/${encodeURIComponent(compteId)}/role`,
      corps,
      { galerie, ...appel },
    ),
  modifierDroits: (
    galerie: string,
    compteId: string,
    corps: { droits: DroitsFins; revision: number },
    appel?: AppelApi,
  ) =>
    api.put<ReponseModificationMembre>(
      `${base(galerie)}/${encodeURIComponent(compteId)}/droits`,
      corps,
      { galerie, ...appel },
    ),
};

export function useModifierRoleMembre(galerie: string) {
  const client = useQueryClient();
  return useMutation<
    ReponseModificationMembre,
    Error,
    { compteId: string; role: RoleGalerie; revision: number }
  >(
    dansLaPortee({
      mutationFn: (
        { compteId, role, revision }: { compteId: string; role: RoleGalerie; revision: number },
        appel,
      ) => equipeApi.modifierRole(galerie, compteId, { role, revision }, appel),
      onSettled: () => client.invalidateQueries({ queryKey: cleMetier(galerie, 'equipe') }),
    }),
  );
}

export function useModifierDroitsMembre(galerie: string) {
  const client = useQueryClient();
  return useMutation<
    ReponseModificationMembre,
    Error,
    { compteId: string; droits: DroitsFins; revision: number }
  >(
    dansLaPortee({
      mutationFn: (
        { compteId, droits, revision }: { compteId: string; droits: DroitsFins; revision: number },
        appel,
      ) => equipeApi.modifierDroits(galerie, compteId, { droits, revision }, appel),
      onSettled: () => client.invalidateQueries({ queryKey: cleMetier(galerie, 'equipe') }),
    }),
  );
}

export function useEquipe(galerie: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cleMetier(galerie, 'equipe'),
    queryFn: (contexte) => equipeApi.lister(galerie, appelDeRequete(contexte)),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}
